import React, { useState, useEffect } from 'react';
import { User, Lock, Bell, Trash2, Save, Loader, CheckCircle, Key, Globe, Moon, Zap, Copy, RefreshCw } from 'lucide-react';
import api from '../api';

export default function Settings() {
  const [user, setUser] = useState(null);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [tab, setTab] = useState('profile');
  const [apiKey] = useState('astra_sk_' + Math.random().toString(36).substr(2, 24));
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const u = localStorage.getItem('user');
    if (u) { const p = JSON.parse(u); setUser(p); setName(p.full_name || ''); }
    api.get('/auth/me').then(r => { setUser(r.data); setName(r.data.full_name || ''); }).catch(() => {});
  }, []);

  const saveProfile = async () => {
    setSaving(true);
    setTimeout(() => {
      const updated = { ...user, full_name: name };
      setUser(updated); localStorage.setItem('user', JSON.stringify(updated));
      setSaving(false); setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    }, 800);
  };

  const copyKey = () => {
    navigator.clipboard.writeText(apiKey);
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: <User size={15} /> },
    { id: 'security', label: 'Security', icon: <Lock size={15} /> },
    { id: 'api', label: 'API Access', icon: <Key size={15} /> },
    { id: 'notifications', label: 'Notifications', icon: <Bell size={15} /> },
    { id: 'preferences', label: 'Preferences', icon: <Globe size={15} /> },
    { id: 'danger', label: 'Danger Zone', icon: <Trash2 size={15} /> },
  ];

  const tierColor = { free: 'var(--text3)', starter: 'var(--green)', growth: 'var(--accent3)', business: 'var(--yellow)' };

  return (
    <div className="fade-in">
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 40, marginBottom: 6 }}>SETTINGS</h1>
        <p style={{ color: 'var(--text2)', fontSize: 15 }}>Manage your account, API keys and preferences</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 20 }}>
        {/* Sidebar */}
        <div>
          <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 8, marginBottom: 12 }}>
            {tabs.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)} style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 9,
                padding: '10px 12px', borderRadius: 9, marginBottom: 2,
                background: tab === t.id ? 'var(--accent-glow)' : 'transparent',
                border: `1px solid ${tab === t.id ? 'rgba(99,102,241,0.3)' : 'transparent'}`,
                color: tab === t.id ? 'var(--accent3)' : 'var(--text2)',
                fontSize: 13, fontWeight: tab === t.id ? 700 : 500, cursor: 'pointer',
                fontFamily: 'Geist', textAlign: 'left', transition: 'all 0.15s',
                ...(t.id === 'danger' ? { color: tab === t.id ? 'var(--red)' : 'rgba(239,68,68,0.6)', background: tab === t.id ? 'var(--red-bg)' : 'transparent', borderColor: tab === t.id ? 'rgba(239,68,68,0.3)' : 'transparent' } : {})
              }}>
                {t.icon} {t.label}
              </button>
            ))}
          </div>

          {/* Account summary card */}
          {user && (
            <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '16px' }}>
              <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'linear-gradient(135deg, var(--accent), var(--purple))', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 800, color: 'white', margin: '0 auto 10px', boxShadow: '0 0 20px rgba(99,102,241,0.4)' }}>
                {name?.[0]?.toUpperCase() || 'U'}
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, textAlign: 'center', marginBottom: 3 }}>{name}</div>
              <div style={{ fontSize: 11, color: 'var(--text3)', textAlign: 'center', marginBottom: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.email}</div>
              <div style={{ textAlign: 'center' }}>
                <span style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: tierColor[user?.tier] || 'var(--text3)', background: `${tierColor[user?.tier]}15`, padding: '3px 10px', borderRadius: 20, border: `1px solid ${tierColor[user?.tier]}30` }}>
                  {user?.tier} plan
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {tab === 'profile' && (
            <>
              <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 24 }}>
                <h2 style={{ marginBottom: 20 }}>Profile Information</h2>
                <div style={{ marginBottom: 16 }}>
                  <label>Full Name</label>
                  <input value={name} onChange={e => setName(e.target.value)} placeholder="Your name" />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label>Email Address</label>
                  <input value={user?.email || ''} disabled style={{ opacity: 0.4, cursor: 'not-allowed' }} />
                  <p style={{ fontSize: 11, color: 'var(--text3)', marginTop: 5 }}>Email address cannot be changed</p>
                </div>
                <div style={{ marginBottom: 24 }}>
                  <label>Account Created</label>
                  <input value={new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} disabled style={{ opacity: 0.4, cursor: 'not-allowed' }} />
                </div>
                <button className="btn btn-primary" onClick={saveProfile} disabled={saving}>
                  {saving ? <><Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> Saving...</>
                    : saved ? <><CheckCircle size={14} /> Saved!</>
                    : <><Save size={14} /> Save Changes</>}
                </button>
              </div>

              {/* Connected accounts */}
              <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 24 }}>
                <h2 style={{ marginBottom: 16 }}>Connected Accounts</h2>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', background: 'var(--bg3)', borderRadius: 10, border: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 20 }}>🔵</span>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600 }}>Google Suite</div>
                      <div style={{ fontSize: 12, color: 'var(--text3)' }}>Gmail, Sheets, Calendar, Drive</div>
                    </div>
                  </div>
                  <button className="btn btn-secondary btn-sm" onClick={() => window.location.href = '/integrations'}>Manage</button>
                </div>
              </div>
            </>
          )}

          {tab === 'security' && (
            <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 24 }}>
              <h2 style={{ marginBottom: 6 }}>Security Settings</h2>
              <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 24 }}>Keep your account safe with a strong password</p>
              <div style={{ marginBottom: 16 }}>
                <label>Current Password</label>
                <input type="password" placeholder="••••••••" />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label>New Password</label>
                <input type="password" placeholder="Min. 8 characters" />
              </div>
              <div style={{ marginBottom: 24 }}>
                <label>Confirm New Password</label>
                <input type="password" placeholder="Repeat new password" />
              </div>
              <button className="btn btn-primary" onClick={() => alert('Password update coming soon')}>
                <Lock size={14} /> Update Password
              </button>

              <div style={{ marginTop: 24, padding: '16px', background: 'var(--bg3)', borderRadius: 10, border: '1px solid var(--border)' }}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Active Sessions</div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontSize: 13, color: 'var(--text2)' }}>Current session</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>Browser · Nigeria · Just now</div>
                  </div>
                  <span style={{ fontSize: 11, color: 'var(--green)', fontWeight: 700, background: 'var(--green-bg)', padding: '3px 10px', borderRadius: 20 }}>Active</span>
                </div>
              </div>
            </div>
          )}

          {tab === 'api' && (
            <>
              <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 24 }}>
                <h2 style={{ marginBottom: 6 }}>API Access</h2>
                <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 24 }}>Use the AstraAutomations API to trigger automations programmatically from your own apps</p>

                <div style={{ marginBottom: 16 }}>
                  <label>Your API Key</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input value={apiKey} readOnly style={{ fontFamily: 'Geist Mono', fontSize: 12, letterSpacing: '0.02em', cursor: 'default' }} />
                    <button className="btn btn-secondary" style={{ flexShrink: 0 }} onClick={copyKey}>
                      {copied ? <><CheckCircle size={14} /> Copied!</> : <><Copy size={14} /> Copy</>}
                    </button>
                  </div>
                  <p style={{ fontSize: 11, color: 'var(--text3)', marginTop: 5 }}>Keep this key secret. Never share it publicly.</p>
                </div>

                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-danger" onClick={() => alert('Rotate key coming soon')}>
                    <RefreshCw size={14} /> Rotate Key
                  </button>
                </div>
              </div>

              <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 24 }}>
                <h2 style={{ marginBottom: 16 }}>Quick API Reference</h2>
                {[
                  { method: 'GET', path: '/api/automations', desc: 'List all automations' },
                  { method: 'POST', path: '/api/automations', desc: 'Create automation' },
                  { method: 'POST', path: '/api/automations/:id/toggle', desc: 'Activate / pause' },
                  { method: 'DELETE', path: '/api/automations/:id', desc: 'Delete automation' },
                ].map(r => (
                  <div key={r.path} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, padding: '10px 14px', background: 'var(--bg3)', borderRadius: 8, border: '1px solid var(--border)' }}>
                    <span style={{ fontSize: 10, fontWeight: 800, color: r.method === 'GET' ? 'var(--blue)' : r.method === 'POST' ? 'var(--green)' : 'var(--red)', background: `${r.method === 'GET' ? 'rgba(96,165,250,' : r.method === 'POST' ? 'rgba(34,197,94,' : 'rgba(239,68,68,'}0.1)`, padding: '2px 7px', borderRadius: 5, fontFamily: 'Geist Mono', flexShrink: 0 }}>{r.method}</span>
                    <span style={{ fontSize: 12, fontFamily: 'Geist Mono', color: 'var(--text2)', flex: 1 }}>{r.path}</span>
                    <span style={{ fontSize: 12, color: 'var(--text3)' }}>{r.desc}</span>
                  </div>
                ))}
              </div>
            </>
          )}

          {tab === 'notifications' && (
            <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 24 }}>
              <h2 style={{ marginBottom: 6 }}>Notification Preferences</h2>
              <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 24 }}>Choose what you want to be notified about</p>
              {[
                { label: 'Automation built', desc: 'When your automation finishes building', enabled: true },
                { label: 'Automation failed', desc: 'When an automation fails to run', enabled: true },
                { label: 'Automation activated', desc: 'Confirmation when you turn one on', enabled: false },
                { label: 'Weekly summary', desc: 'Weekly overview of automation activity', enabled: true },
                { label: 'New integrations', desc: 'When new platforms are added', enabled: false },
                { label: 'Product updates', desc: 'New features and improvements', enabled: true },
              ].map((item, i, arr) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 0', borderBottom: i < arr.length - 1 ? '1px solid var(--border)' : 'none' }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 2 }}>{item.label}</div>
                    <div style={{ fontSize: 12, color: 'var(--text2)' }}>{item.desc}</div>
                  </div>
                  <div style={{ width: 44, height: 24, background: item.enabled ? 'linear-gradient(135deg, var(--accent), var(--accent2))' : 'var(--bg4)', borderRadius: 12, cursor: 'pointer', position: 'relative', border: '1px solid var(--border2)', flexShrink: 0, transition: 'background 0.2s', boxShadow: item.enabled ? '0 0 12px rgba(99,102,241,0.3)' : 'none' }}>
                    <div style={{ position: 'absolute', width: 18, height: 18, borderRadius: '50%', background: 'white', top: 2, left: item.enabled ? 22 : 2, transition: 'left 0.2s' }} />
                  </div>
                </div>
              ))}
            </div>
          )}

          {tab === 'preferences' && (
            <>
              <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 24 }}>
                <h2 style={{ marginBottom: 20 }}>Display Preferences</h2>
                <div style={{ marginBottom: 16 }}>
                  <label>Timezone</label>
                  <select style={{ background: 'var(--bg3)', border: '1px solid var(--border)', color: 'var(--text)', borderRadius: 9, padding: '11px 15px', fontSize: 14, fontFamily: 'Geist', outline: 'none', width: '100%' }}>
                    <option>Africa/Lagos (WAT, UTC+1)</option>
                    <option>America/New_York (EST, UTC-5)</option>
                    <option>America/Los_Angeles (PST, UTC-8)</option>
                    <option>Europe/London (GMT, UTC+0)</option>
                    <option>Europe/Berlin (CET, UTC+1)</option>
                    <option>Asia/Dubai (GST, UTC+4)</option>
                  </select>
                </div>
                <div style={{ marginBottom: 24 }}>
                  <label>Language</label>
                  <select style={{ background: 'var(--bg3)', border: '1px solid var(--border)', color: 'var(--text)', borderRadius: 9, padding: '11px 15px', fontSize: 14, fontFamily: 'Geist', outline: 'none', width: '100%' }}>
                    <option>English (US)</option>
                    <option>English (UK)</option>
                  </select>
                </div>
                <button className="btn btn-primary" onClick={() => alert('Preferences saved!')}>
                  <Save size={14} /> Save Preferences
                </button>
              </div>

              <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 24 }}>
                <h2 style={{ marginBottom: 16 }}>Dashboard Layout</h2>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  {['Compact view', 'Expanded view'].map((v, i) => (
                    <div key={v} style={{ padding: '14px', background: i === 0 ? 'var(--accent-glow)' : 'var(--bg3)', border: `1px solid ${i === 0 ? 'rgba(99,102,241,0.35)' : 'var(--border)'}`, borderRadius: 9, cursor: 'pointer', textAlign: 'center' }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: i === 0 ? 'var(--accent3)' : 'var(--text2)', marginBottom: 3 }}>{v}</div>
                      <div style={{ fontSize: 11, color: 'var(--text3)' }}>{i === 0 ? 'More automations visible' : 'More details shown'}</div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {tab === 'danger' && (
            <>
              <div style={{ background: 'var(--bg2)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 'var(--radius)', padding: 24 }}>
                <h2 style={{ marginBottom: 6, color: 'var(--red)' }}>⚠️ Danger Zone</h2>
                <p style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 20 }}>These actions are permanent and cannot be undone. Proceed with caution.</p>

                {[
                  { title: 'Delete all automations', desc: 'Remove all automations from your account and n8n instance. Your account remains active.', btn: 'Delete All Automations', color: 'var(--yellow)' },
                  { title: 'Delete Account', desc: 'Permanently delete your account, all automations, and all data. This cannot be reversed.', btn: 'Delete My Account', color: 'var(--red)' },
                ].map(item => (
                  <div key={item.title} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '16px', background: 'var(--bg3)', borderRadius: 10, border: '1px solid var(--border)', marginBottom: 10 }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>{item.title}</div>
                      <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.5 }}>{item.desc}</div>
                    </div>
                    <button style={{ background: 'var(--red-bg)', color: 'var(--red)', border: '1px solid rgba(239,68,68,0.25)', padding: '8px 16px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'Geist', flexShrink: 0 }}
                      onClick={() => alert('Please contact support@astrautomations.com')}>
                      {item.btn}
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
